import React from "react";

import { TranslatableContentType, TranslatedContentType } from "../useTranslatedContent";
import { ILanguage, ILcidTranslateDetails } from "modules/localization/models";

/**
 * Encapsulate logic for optional translate all functionality.
 * Designed to be used generally in tandem with useTranslatedContent.
 * @param {
 *      translatedContent: TranslatableContentType
 *      availableLanguages: ILanguages[] | undefined
 *      defaultLcid: string
 * 
 *      //May need to write a wrapper when using this hook to make sure you're passing the correct formatting.
 *      //There should be existing usages of this elsewhere to reference.
 *      getAllTranslations: (toTranslate: TranslatableContentType, outputLanguages: ILcidTranslateDetails[]) 
 *                              => Promise<TranslatedContentType<T>[]) // T extends TranslatableContentType
 * }
 * @returns {
 *      isFetchingTranslations: boolean
 *      translateAll: (outputLanguages: ILcidTranslateDetails[]) => Promise<TranslatedContentType<T>> // T extends TranslatableContentType
 * 
 *      //Used for the translate all issue dialog.
 *      errorModalStatus: TranslateAllIssueDialogInput;
 * }
 */

export type TranslateAllIssueDialogInput = {
    showModal: boolean;
    expectedLanguages: number;
    actualLanguages: number;
    updateModalOpenStatus: (open: boolean) => void;
}

export type TranslateAllProps<T extends TranslatableContentType> = {
    translatedContent: TranslatableContentType;
    availableLanguages?: ILanguage[];
    defaultLcid: string;
    getAllTranslations: (toTranslate: TranslatableContentType, outputLanguages: ILcidTranslateDetails[]) => Promise<TranslatedContentType<T>[]>;
}

export type TranslateAllStatus<T extends TranslatableContentType> = {
    isFetchingTranslations: boolean;
    translateAll: (outputLanguages: ILcidTranslateDetails[]) => Promise<TranslatedContentType<T>[] | null>
    errorModalStatus: TranslateAllIssueDialogInput;
}

const useTranslateAll = <T extends TranslatableContentType>(props: TranslateAllProps<T>): TranslateAllStatus<T> => {
    const [isFetchingTranslation, setIsFetchingTranslation] = React.useState<boolean>(false);
    const [showTranslateAllIssueModal, setShowTranslateAllIssueModal] = React.useState<boolean>(false);
    const [availableLanguagesCount, setAvailableLanguagesCount] = React.useState<number>(0);
    const [returnedTranslationsCount, setReturnedTranslationsCount] = React.useState<number>(0);

    const isEmptyResponse = (translations: TranslatedContentType<T>[]) => {
        return !translations || Object.keys(translations).length === 0;
    }

    const checkTranslationResultsIncludeAllLanguages = (expectedLanguageCount: number, resultLanguageCount: number) => {
        if (expectedLanguageCount !== resultLanguageCount) {
            setAvailableLanguagesCount(expectedLanguageCount);
            setReturnedTranslationsCount(resultLanguageCount);
            setShowTranslateAllIssueModal(true);
        }
    }

    const translateAll = async(outputLanguages: ILcidTranslateDetails[]) => {
        if (props.translatedContent && props.availableLanguages) {

            setIsFetchingTranslation(true);
            const countOfNonDefaultLanguages = props.availableLanguages.length - 1;

            let finishedTranslations: TranslatedContentType<T>[];

            finishedTranslations = await fetchTranslations(outputLanguages);

            if (isEmptyResponse(finishedTranslations)) {
                setIsFetchingTranslation(false);
                return null;
            }

            checkTranslationResultsIncludeAllLanguages(countOfNonDefaultLanguages, Object.keys(finishedTranslations).length);
            setIsFetchingTranslation(false);
            
            //Allow the components calling the hook to update their translated content themselves.
            return finishedTranslations;
        }
        else {
            return null;
        }
    }

    const fetchTranslations = async (outputLanguages: ILcidTranslateDetails[]) => {
        try {
            return await props.getAllTranslations(props.translatedContent, outputLanguages);
        }
        catch (err) {
            return [];
        }
    }

    const errorModalStatus: TranslateAllIssueDialogInput = {
        showModal: showTranslateAllIssueModal,
        expectedLanguages: availableLanguagesCount,
        actualLanguages: returnedTranslationsCount,
        updateModalOpenStatus: setShowTranslateAllIssueModal
    }

    return {
        isFetchingTranslations: isFetchingTranslation,
        translateAll,
        errorModalStatus
    };
}

export default useTranslateAll;